import type { Ref } from 'vue'
import { ref } from 'vue'

declare global {
  interface Window {
    google?: any
    googleTranslateElementInit?: () => void
  }
}

interface GoogleTranslateOptions {
  pageLanguage?: string
  includedLanguages?: string
}

export function useGoogleTranslate(options: GoogleTranslateOptions = {}) {
  const {
    pageLanguage = 'en',
    includedLanguages = 'en,bn'
  } = options

  const allowedLanguages = includedLanguages.split(',').map((l) => l.trim())
  const reloadGuardKey = 'gt_reload_lang'
  const scriptSources = [
    'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit',
    'https://translate.googleapis.com/translate_a/element.js?cb=googleTranslateElementInit'
  ]

  const getCookieDomains = (): string[] => {
    const hostname = window.location.hostname
    if (!hostname || hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return ['']
    }

    const parts = hostname.split('.').filter(Boolean)
    const domains = new Set<string>(['', `.${hostname}`])
    for (let i = 1; i < parts.length - 1; i++) {
      domains.add(`.${parts.slice(i).join('.')}`)
    }
    return Array.from(domains)
  }

  const getLanguageFromCookie = (): string | null => {
    const match = document.cookie.match(/(?:^|;\s*)googtrans=\/[^/]+\/([^;]+)/)
    if (!match || !match[1]) {
      return null
    }
    return match[1]
  }

  const setLanguageCookie = (lang: string) => {
    const value = `/${pageLanguage}/${lang}`
    const secure = window.location.protocol === 'https:' ? ';Secure' : ''
    for (const domain of getCookieDomains()) {
      document.cookie = domain
        ? `googtrans=${value};path=/;domain=${domain};SameSite=Lax${secure}`
        : `googtrans=${value};path=/;SameSite=Lax${secure}`
    }
  }

  const clearLanguageCookie = () => {
    const secure = window.location.protocol === 'https:' ? ';Secure' : ''
    for (const domain of getCookieDomains()) {
      document.cookie = domain
        ? `googtrans=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain};SameSite=Lax${secure}`
        : `googtrans=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax${secure}`
    }
  }

  const currentLanguage: Ref<string> = ref(getLanguageFromCookie() || pageLanguage)
  const translatorReady: Ref<boolean> = ref(false)
  const translatorError: Ref<string | null> = ref(null)
  const retryCount: Ref<number> = ref(0)
  const maxRetries = 3

  const applyLanguageViaSelect = async (lang: string, maxAttempts = 25): Promise<boolean> => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement | null
      if (selectElement) {
        selectElement.value = lang
        selectElement.dispatchEvent(new Event('change'))
        return true
      }

      await new Promise<void>((resolve) => {
        setTimeout(resolve, 120)
      })
    }

    return false
  }

  // Define callback globally
  const setupCallback = () => {
    ;(window as any).googleTranslateElementInit = initializeTranslator
  }

  // Initialize the translator
  const initializeTranslator = () => {
    try {
      if (!window.google?.translate?.TranslateElement) {
        throw new Error('Google Translate API not fully loaded')
      }

      new window.google.translate.TranslateElement(
        {
          pageLanguage,
          includedLanguages,
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE
        },
        'google_translate_element'
      )

      translatorReady.value = true
      translatorError.value = null

      const cookieLang = getLanguageFromCookie()
      if (cookieLang && allowedLanguages.includes(cookieLang)) {
        currentLanguage.value = cookieLang
      }

      if (currentLanguage.value !== pageLanguage) {
        setTimeout(async () => {
          await applyLanguageViaSelect(currentLanguage.value)
          sessionStorage.removeItem(reloadGuardKey)
        }, 300)
      } else {
        sessionStorage.removeItem(reloadGuardKey)
      }

      console.log('✓ Google Translate initialized successfully')
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error)
      translatorError.value = errorMsg
      console.error('✗ Failed to initialize Google Translate:', errorMsg)

      // Retry if not exceeded max attempts
      if (retryCount.value < maxRetries) {
        retryCount.value++
        setTimeout(initializeTranslator, 1000 * retryCount.value)
      }
    }
  }

  // Load the Google Translate script
  const loadTranslatorScript = (sourceIndex = 0) => {
    // Setup callback before loading script
    setupCallback()

    // Check if already loaded
    if (window.google?.translate) {
      initializeTranslator()
      return
    }

    // Create and append script
    const existing = document.getElementById('google-translate-script') as HTMLScriptElement | null
    if (existing) {
      existing.remove()
    }

    const script = document.createElement('script')
    script.id = 'google-translate-script'
    script.type = 'text/javascript'
    script.async = true
    script.src = scriptSources[sourceIndex] ?? scriptSources[0] ?? 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'

    script.onload = () => {
      console.log('Google Translate script loaded')
    }

    script.onerror = () => {
      const next = sourceIndex + 1
      if (next < scriptSources.length) {
        loadTranslatorScript(next)
        return
      }
      const msg = 'Failed to load Google Translate script from all sources'
      translatorError.value = msg
      console.error('✗', msg)
    }

    document.head.appendChild(script)
  }

  // Change language with alternative methods
  const changeLanguage = async (lang: string) => {
    const targetLang = lang === 'bn' ? 'bn' : pageLanguage
    currentLanguage.value = targetLang

    // Resetting back to the source language is unreliable through the widget
    // because Google Translate mutates the live DOM. Clear the cookie and do
    // one controlled reload so Vue mounts against the original markup again.
    if (targetLang === pageLanguage) {
      clearLanguageCookie()

      const alreadyReloadedFor = sessionStorage.getItem(reloadGuardKey)
      if (alreadyReloadedFor !== targetLang) {
        sessionStorage.setItem(reloadGuardKey, targetLang)
        window.location.reload()
        return
      }

      sessionStorage.removeItem(reloadGuardKey)
      console.log(`✓ Language reset to source language: ${targetLang}`)
      return
    }

    setLanguageCookie(targetLang)

    if (!translatorReady.value) {
      loadTranslatorScript()
    }

    const changed = await applyLanguageViaSelect(targetLang, 30)
    if (changed) {
      sessionStorage.removeItem(reloadGuardKey)
      console.log(`✓ Language changed to: ${targetLang}`)
      return
    }

    const alreadyReloadedFor = sessionStorage.getItem(reloadGuardKey)
    if (alreadyReloadedFor !== targetLang) {
      sessionStorage.setItem(reloadGuardKey, targetLang)
      console.warn('Google Translate dropdown unavailable. Reloading once with cookie fallback.')
      window.location.reload()
      return
    }

    console.warn('Google Translate dropdown still unavailable after reload. Language cookie has been saved.')
  }

  // Initialize on first call
  const initialize = () => {
    if (!translatorReady.value) {
      loadTranslatorScript()
    }
  }

  return {
    currentLanguage,
    translatorReady,
    translatorError,
    initialize,
    changeLanguage
  }
}
